import React, { useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { HiPlus, HiTrash, HiUser, HiPencil } from 'react-icons/hi';
import { useGeneral } from '../../../contexts';
import { OrganizerPanelOutletContext } from '../../../types/organizerPanel.types';
import { useSegmentations } from '../../../hooks/useSegmentations';
import Services from '../../../services/Services';
import Button from '../../../components/ui/Button/Button';
import FormInput from '../../../components/ui/Form/FormInput';
import LoadingSkeleton from '../../../components/ui/LoadingSkeleton/LoadingSkeleton';
import Modal from '../../../components/ui/Modal/Modal';
import TagPill from '../../../components/ui/TagPill';
import { Table, TableHeader, TableBody, TableRow, TableCell, EmptyTableRow } from '../../../components/ui/Table';
import styles from './SegmentsPage.module.css';

const COLUMN_WIDTHS = ['1fr', '120px', '100px'];

const SegmentsPage: React.FC = () => {
  const { channel } = useOutletContext<OrganizerPanelOutletContext>();
  const { auth, language, toastSuccess, toastError } = useGeneral();
  const { token } = auth;
  const { getTranslation } = language;
  const navigate = useNavigate();

  const {
    segmentsWithCount,
    loading,
    addSegment,
    removeSegment,
    refreshSegments,
  } = useSegmentations({ token, channelID: channel?.channelID });

  const [newSegmentName, setNewSegmentName] = useState('');
  const [addingSegment, setAddingSegment] = useState(false);
  const [removingSegment, setRemovingSegment] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  // Rename state
  const [renameModal, setRenameModal] = useState<{ isOpen: boolean; tag: string }>({ isOpen: false, tag: '' });
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);

  const handleAddSegment = async () => {
    const trimmedName = newSegmentName.trim();
    if (!trimmedName) return;

    if (segmentsWithCount.some(s => s.tag === trimmedName)) {
      return;
    }

    setAddingSegment(true);
    const success = await addSegment(trimmedName);
    setAddingSegment(false);

    if (success) {
      setNewSegmentName('');
      toastSuccess(getTranslation('organizerPanel.segmentCreated') || 'Segment created successfully');
    } else {
      toastError(getTranslation('organizerPanel.tagUpdateError') || 'Failed to create segment');
    }
  };

  const handleRemoveSegment = async (tag: string) => {
    setRemovingSegment(tag);
    const success = await removeSegment(tag);
    setRemovingSegment(null);
    setConfirmDelete(null);

    if (success) {
      toastSuccess(getTranslation('organizerPanel.segmentRemoved') || 'Segment removed successfully');
    } else {
      toastError(getTranslation('organizerPanel.tagUpdateError') || 'Failed to remove segment');
    }
  };

  const handleOpenRename = (tag: string) => {
    setRenameValue(tag);
    setRenameModal({ isOpen: true, tag });
  };

  const handleRenameSegment = async () => {
    const trimmed = renameValue.trim();
    if (!trimmed || !token || !channel?.channelID) return;
    if (trimmed === renameModal.tag) {
      setRenameModal({ isOpen: false, tag: '' });
      return;
    }
    if (segmentsWithCount.some(s => s.tag === trimmed)) {
      toastError(getTranslation('organizerPanel.segmentAlreadyExists') || 'A segment with this name already exists');
      return;
    }

    setRenaming(true);
    try {
      const response = await Services.SocialHiveUpdateChannelSegment(
        token,
        channel.channelID,
        renameModal.tag,
        trimmed
      );
      if (response.data.status) {
        toastSuccess(getTranslation('organizerPanel.segmentRenamed') || 'Segment renamed successfully');
        setRenameModal({ isOpen: false, tag: '' });
        refreshSegments();
      } else {
        toastError(getTranslation('organizerPanel.tagUpdateError') || 'Failed to rename segment');
      }
    } catch {
      toastError(getTranslation('organizerPanel.tagUpdateError') || 'Failed to rename segment');
    } finally {
      setRenaming(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !addingSegment) {
      handleAddSegment();
    }
  };

  const handleRenameKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !renaming) {
      handleRenameSegment();
    }
  };

  const handleSegmentClick = (tag: string) => {
    navigate(encodeURIComponent(tag));
  };

  if (loading) {
    return (
      <div className={styles.segmentsPage}>
        <div className={styles.addSection}>
          <div className={styles.addInput}>
            <LoadingSkeleton height={42} width="100%" />
          </div>
          <LoadingSkeleton height={42} width={140} />
        </div>
        <div className={styles.skeletonTable}>
          <div className={styles.skeletonHeader}>
            <LoadingSkeleton height={14} width={120} variant="text" />
            <LoadingSkeleton height={14} width={80} variant="text" />
          </div>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className={styles.skeletonRow}>
              <LoadingSkeleton height={28} width={80 + i * 20} />
              <LoadingSkeleton height={20} width={40} variant="text" />
              <LoadingSkeleton height={32} width={32} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.segmentsPage}>
      <div className={styles.addSection}>
        <div className={styles.addInput}>
          <FormInput
            value={newSegmentName}
            onChange={(e) => setNewSegmentName(e.target.value)}
            placeholder={getTranslation('organizerPanel.enterSegmentName') || 'Enter segment name...'}
            onKeyPress={handleKeyPress}
            disabled={addingSegment}
          />
        </div>
        <Button
          variant="primary"
          size="medium"
          onClick={handleAddSegment}
          disabled={!newSegmentName.trim() || addingSegment}
          className={styles.addButton}
        >
          {/* @ts-ignore - react-icons compatibility with React 19 */}
          <HiPlus size={18} />
          {getTranslation('organizerPanel.addSegment') || 'Add Segment'}
        </Button>
      </div>

      <Table>
        <TableHeader
          columns={[
            `${(getTranslation('organizerPanel.segments') || 'SEGMENTS').toUpperCase()} – ${segmentsWithCount.length}`,
            (getTranslation('organizerPanel.members') || 'MEMBERS').toUpperCase(),
            '',
          ]}
          columnWidths={COLUMN_WIDTHS}
        />
        <TableBody>
          {segmentsWithCount.length === 0 ? (
            <EmptyTableRow
              message={getTranslation('organizerPanel.noSegments') || 'No segments created yet'}
            />
          ) : (
            segmentsWithCount.map((segment) => (
              <TableRow
                key={segment.tag}
                columnWidths={COLUMN_WIDTHS}
                onClick={() => handleSegmentClick(segment.tag)}
              >
                <TableCell>
                  <div className={styles.segmentName}>
                    <TagPill tag={segment.tag} size="medium" />
                  </div>
                </TableCell>
                <TableCell>
                  <span className={styles.memberCount}>
                    {segment.userCount}
                    {/* @ts-ignore - react-icons compatibility with React 19 */}
                    <HiUser size={16} className={styles.memberIcon} />
                  </span>
                </TableCell>
                <TableCell align="right">
                  <div className={styles.actionButtons}>
                    <button
                      type="button"
                      className={styles.editButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenRename(segment.tag);
                      }}
                      aria-label={`Rename ${segment.tag}`}
                    >
                      {/* @ts-ignore - react-icons compatibility with React 19 */}
                      <HiPencil size={16} />
                    </button>
                    <button
                      type="button"
                      className={styles.deleteButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmDelete(segment.tag);
                      }}
                      disabled={removingSegment === segment.tag}
                      aria-label={`Delete ${segment.tag}`}
                    >
                      {/* @ts-ignore - react-icons compatibility with React 19 */}
                      <HiTrash size={16} />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* Delete Confirm Modal */}
      <Modal
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title={getTranslation('organizerPanel.removeSegment') || 'Remove Segment'}
        variant="confirm"
        message={getTranslation('organizerPanel.confirmRemoveSegment') || 'Are you sure you want to remove this segment? It will be removed from all users.'}
        confirmText={getTranslation('common.remove') || 'Remove'}
        cancelText={getTranslation('common.cancel') || 'Cancel'}
        onConfirm={() => confirmDelete && handleRemoveSegment(confirmDelete)}
        onCancel={() => setConfirmDelete(null)}
        isDanger
        disabled={removingSegment !== null}
      />

      {/* Rename Modal */}
      <Modal
        isOpen={renameModal.isOpen}
        onClose={() => setRenameModal({ isOpen: false, tag: '' })}
        title={getTranslation('organizerPanel.renameSegment') || 'Rename Segment'}
        variant="default"
      >
        <div className={styles.renameModalContent}>
          <FormInput
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            placeholder={getTranslation('organizerPanel.enterSegmentName') || 'Enter segment name...'}
            onKeyPress={handleRenameKeyPress}
            disabled={renaming}
            autoFocus
          />
          <div className={styles.renameModalActions}>
            <Button
              variant="secondary"
              size="medium"
              onClick={() => setRenameModal({ isOpen: false, tag: '' })}
              disabled={renaming}
            >
              {getTranslation('common.cancel') || 'Cancel'}
            </Button>
            <Button
              variant="primary"
              size="medium"
              onClick={handleRenameSegment}
              disabled={!renameValue.trim() || renameValue.trim() === renameModal.tag || renaming}
            >
              {getTranslation('common.save') || 'Save'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SegmentsPage;
